import React, { useState, useEffect, useRef } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, Button } from '@mui/material';
import { Close, QrCodeScanner, FlipCameraIos } from '@mui/icons-material';
import { BrowserMultiFormatReader, Exception } from '@zxing/library';

interface QRScannerProps {
  onScan: (data: string) => void;
  onClose: () => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onScan, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState<boolean>(false);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const hasScanned = useRef<boolean>(false);
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  
  // Initialize camera devices first
  useEffect(() => {
    const getCameraDevices = async () => {
      try {
        // Request permission first
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream;
        
        // Once we have permission, get the list of devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        if (videoDevices.length === 0) {
          setError('No camera devices found');
          return;
        }
        
        setCameraDevices(videoDevices);
        
        // Prefer back camera if available
        const backCameraIndex = videoDevices.findIndex(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear')
        );
        
        if (backCameraIndex !== -1) {
          setCurrentCameraIndex(backCameraIndex);
        }
      } catch (err) {
        console.error('Error getting camera devices:', err);
        setError('Could not access camera. Please check permissions.');
      }
    };
    
    getCameraDevices();
    
    // Clean up initial permission stream
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
        streamRef.current = null;
      }
    };
  }, []);
  
  // Once we have camera devices, initialize QR scanner
  useEffect(() => {
    if (cameraDevices.length === 0) return;
    
    // Create a new reader
    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;
    let isMounted = true;
    
    // Reset the hasScanned flag
    hasScanned.current = false;
    
    const startScanner = async () => {
      if (!isMounted || !videoRef.current) return;
      
      try {
        // Reset any previous errors
        setError(null);
        setScanning(true);
        
        // Get current device ID
        const deviceId = cameraDevices[currentCameraIndex]?.deviceId;
        
        if (!deviceId) {
          console.error('No valid camera device ID found');
          setError('Camera device not available');
          return;
        }
        
        console.log(`Starting camera with device ID: ${deviceId}`);
        
        // First try to get the video stream directly
        try {
          // First get direct video stream with exact deviceId
          const constraints: MediaStreamConstraints = {
            video: { deviceId: { exact: deviceId } },
            audio: false
          };
          
          console.log('Requesting media with constraints:', JSON.stringify(constraints));
          
          // Get stream directly first to ensure camera works
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log('Stream obtained successfully');
          
          // Set stream to video element
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            
            // Make sure video is playing
            try {
              await videoRef.current.play();
              console.log('Video playback started');
            } catch (playError) {
              console.error('Error playing video:', playError);
              // Try again without awaiting
              videoRef.current.play().catch(e => console.error('Second play attempt failed:', e));
            }
          }
          
          // Store stream for cleanup
          streamRef.current = stream;
          
        } catch (streamError) {
          console.error('Error getting specific device stream:', streamError);
          
          // Fallback to any camera if specific device fails
          try {
            console.log('Falling back to any available camera');
            const fallbackStream = await navigator.mediaDevices.getUserMedia({ 
              video: true,
              audio: false 
            });
            
            if (videoRef.current) {
              videoRef.current.srcObject = fallbackStream;
              await videoRef.current.play();
              console.log('Fallback video playback started');
            }
            
            streamRef.current = fallbackStream;
          } catch (fallbackError) {
            console.error('Fallback camera also failed:', fallbackError);
            throw fallbackError; // Re-throw to be caught by outer try/catch
          }
        }
        
        // For debugging: log if video is actually playing
        if (videoRef.current) {
          const checkVideoPlaying = setInterval(() => {
            if (videoRef.current) {
              const videoWidth = videoRef.current.videoWidth;
              const videoHeight = videoRef.current.videoHeight;
              console.log(`Video dimensions: ${videoWidth}x${videoHeight}, readyState: ${videoRef.current.readyState}`);
              
              if (videoWidth > 0 && videoHeight > 0) {
                console.log('Video is showing content');
                clearInterval(checkVideoPlaying);
              }
            } else {
              clearInterval(checkVideoPlaying);
            }
          }, 500);
          
          // Clear the interval after 10 seconds if it hasn't already been cleared
          setTimeout(() => clearInterval(checkVideoPlaying), 10000);
        }
        
        // Start QR code detection with a bit more delay
        setTimeout(async () => {
          if (!isMounted || !videoRef.current) return;
          
          try {
            // In case we're using a fallback stream, get the actual device ID now
            const actualDeviceId = streamRef.current?.getVideoTracks()[0]?.getSettings()?.deviceId || deviceId;
            
            console.log(`Starting QR decoder with device ID: ${actualDeviceId}`);
            
            // Start decoding
            await codeReader.decodeFromVideoDevice(
              actualDeviceId, 
              videoRef.current, 
              (result, err) => {
                if (!isMounted) return;
                
                if (result && !hasScanned.current) {
                  // Prevent multiple scans
                  hasScanned.current = true;
                  
                  // Log the result
                  console.log('QR code detected:', result.getText());
                  
                  // Stop the camera immediately
                  cleanupCamera();
                  
                  // Call the callback
                  onScan(result.getText());
                }
                
                if (err && !(err instanceof Exception)) {
                  // Only log unexpected errors, not the normal "no QR code found" exceptions
                  console.error('QR scanner error:', err);
                }
              }
            );
          } catch (decodeError) {
            console.error('Error starting QR decoder:', decodeError);
            
            // Try with a null deviceId (let the library choose)
            try {
              console.log('Attempting to start decoder with null deviceId');
              await codeReader.decodeFromVideoDevice(
                null,  // Let the library choose the device
                videoRef.current, 
                (result, err) => {
                  if (!isMounted) return;
                  
                  if (result && !hasScanned.current) {
                    hasScanned.current = true;
                    console.log('QR code detected (fallback method):', result.getText());
                    cleanupCamera();
                    onScan(result.getText());
                  }
                  
                  if (err && !(err instanceof Exception)) {
                    console.error('QR scanner fallback error:', err);
                  }
                }
              );
            } catch (fallbackError) {
              console.error('Fallback decoder also failed:', fallbackError);
              setError('Failed to start QR scanner. Please try again or switch cameras.');
            }
          }
        }, 1000); // Longer delay to ensure video is properly initialized
        
      } catch (err) {
        console.error('Failed to start camera:', err);
        if (isMounted) {
          setError('Could not access camera. Please check permissions.');
          setScanning(false);
        }
      }
    };
    
    // Function to clean up camera resources
    const cleanupCamera = () => {
      setScanning(false);
      
      // Reset QR scanner
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.reset();
        } catch (e) {
          console.error('Error resetting code reader:', e);
        }
      }
      
      // Stop video tracks
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
            }
          });
          streamRef.current = null;
        } catch (e) {
          console.error('Error stopping camera tracks:', e);
        }
      }
      
      // Clear video source
      if (videoRef.current && videoRef.current.srcObject) {
        try {
          videoRef.current.srcObject = null;
        } catch (e) {
          console.error('Error clearing video source:', e);
        }
      }
    };
    
    // Start scanning
    startScanner();
    
    // Cleanup when component unmounts or dependencies change
    return () => {
      isMounted = false;
      cleanupCamera();
      if (codeReaderRef.current) {
        codeReaderRef.current = null;
      }
    };
  }, [cameraDevices, currentCameraIndex]);
  
  // Switch camera
  const switchCamera = () => {
    // First clean up the current camera
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {
        console.error('Error resetting code reader:', e);
      }
    }
    
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
        streamRef.current = null;
      } catch (e) {
        console.error('Error stopping camera tracks:', e);
      }
    }
    
    // Switch to next camera
    setCurrentCameraIndex((prevIndex) => 
      (prevIndex + 1) % cameraDevices.length
    );
  };
  
  // Handle manual close
  const handleClose = () => {
    // Stop scanner and release camera
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {
        console.error('Error resetting code reader:', e);
      }
      codeReaderRef.current = null;
    }
    
    // Stop video tracks
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
        streamRef.current = null;
      } catch (e) {
        console.error('Error stopping camera tracks:', e);
      }
    }
    
    // Clear video source
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        videoRef.current.srcObject = null;
      } catch (e) {
        console.error('Error clearing video source:', e);
      }
    }
    
    onClose();
  };

  return (
    <Dialog 
      open={true} 
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          borderRadius: '16px',
          overflow: 'hidden',
        }
      }}
      sx={{ 
        '& .MuiDialog-paper': { 
          overflow: 'hidden'
        },
        '& .MuiDialogContent-root': {
          padding: 0,
          overflow: 'hidden'
        }
      }}
    >
      {error && (
        <Box sx={{ 
          p: 2, 
          bgcolor: 'error.main', 
          color: 'white',
          width: '100%',
          textAlign: 'center',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10
        }}>
          <Typography fontWeight={500}>{error}</Typography>
        </Box>
      )}
      
      <DialogTitle sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
        color: 'white',
        p: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScanner />
          <Typography variant="h6">Scan QR Code</Typography>
        </Box>
        <IconButton onClick={handleClose} color="inherit" edge="end">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'hidden', height: '450px' }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#000', overflow: 'hidden' }}>
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            autoPlay
            playsInline
            muted
          />
          
          {/* Camera switch button */}
          {cameraDevices.length > 1 && (
            <IconButton
              onClick={switchCamera}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 5,
              }}
            >
              <FlipCameraIos />
            </IconButton>
          )}
          
          {/* Scanner overlay with square in the middle */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: '220px',
                height: '220px',
                border: '3px solid #E31B88',
                borderRadius: '12px',
                boxShadow: '0 0 0 1000px rgba(0, 0, 0, 0.5)',
                animation: 'pulse 2s infinite',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '20px',
                  height: '20px',
                  borderTop: '3px solid #E31B88',
                  borderLeft: '3px solid #E31B88',
                  top: -3,
                  left: -3,
                  borderTopLeftRadius: '8px'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '20px',
                  height: '20px',
                  borderBottom: '3px solid #E31B88',
                  borderRight: '3px solid #E31B88',
                  bottom: -3,
                  right: -3,
                  borderBottomRightRadius: '8px'
                },
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(227, 27, 136, 0.7), 0 0 0 1000px rgba(0, 0, 0, 0.5)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 10px rgba(227, 27, 136, 0), 0 0 0 1000px rgba(0, 0, 0, 0.5)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(227, 27, 136, 0), 0 0 0 1000px rgba(0, 0, 0, 0.5)',
                  },
                },
              }}
            />
          </Box>
          
          {/* Instruction text overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: 0,
              right: 0,
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography sx={{
              color: 'white',
              fontWeight: 500,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'inline-block',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '0.9rem'
            }}>
              Position QR code within the square
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QRScanner;
